// My Builds drawer (desktop): slides in from the right after a save. Moved out of App.js unchanged.
import React from 'react'
import ReactDOM from 'react-dom'
import { ensureFreshLogin } from './authRenew'
import { builderBus } from './builderBus'
import { SCREEN_TYPE_OPTIONS as screenTypeOptions } from './screenOptions'

// ======= END =======
// hell yeah

/* ============================================================
   BuildsDrawer — slides in from right after save on desktop
   ============================================================ */
export default function BuildsDrawer({
  open,
  refreshKey,
  onClose,
  onOpenMyBuilds,
  onLoadConfirm,
  onAddToCart,
  colorNames,
  currentDesignId,
  hasUnsavedChanges,
  totalBuildCount
}) {
  const API = 'https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com'
  const LOCAL_KEY = 'ohma_local_builds'
  const LOCAL_BUILDS_LIMIT = 2
  const FONT_H = "'roc-grotesk-wide', sans-serif"
  const FONT_B = "'Jost', system-ui, sans-serif"
  const COLOR_BLUE = '#91c3de'
  const COLOR_SALMON = '#E89B88'

  const [designs, setDesigns] = React.useState([])
  const [loggedIn, setLoggedIn] = React.useState(false)
  const [loaded, setLoaded] = React.useState(false)
  const [confirmDeleteId, setConfirmDeleteId] = React.useState(null)
  const [deleting, setDeleting] = React.useState(false)
  const [openMenuId, setOpenMenuId] = React.useState(null)
  const [drawerDupeToastId, setDrawerDupeToastId] = React.useState(null)
  const [drawerDupeToastPos, setDrawerDupeToastPos] = React.useState(null)
  const [drawerMenuPos, setDrawerMenuPos] = React.useState(null)
  const [renameModalDesign, setRenameModalDesign] = React.useState(null)
  const [renameInputValue, setRenameInputValue] = React.useState('')
  const [duplicateLimitToast, setDuplicateLimitToast] = React.useState(false)
  const [highlightSignIn, setHighlightSignIn] = React.useState(false)

  // Live activity ping — heartbeat every 30s so the admin live counter shows
  // who's currently on the configurator. Uses sessionStorage for a session ID
  // that resets on tab close (in-session unique). Server tracks last-seen
  // per session and counts sessions active in the last 90s.
  React.useEffect(() => {
    let sessionId
    try {
      sessionId = sessionStorage.getItem('ohma_session_id')
      if (!sessionId) {
        sessionId = Math.random().toString(36).slice(2, 10) + Date.now().toString(36)
        sessionStorage.setItem('ohma_session_id', sessionId)
      }
    } catch (e) {
      sessionId = 'no-storage-' + Date.now()
    }
    const sendPing = () => {
      try {
        fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/ping', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId,
            isLoggedIn: !!loggedIn,
            page: 'configurator'
          })
        }).catch(() => {})
      } catch (e) {}
    }
    sendPing()
    const interval = setInterval(sendPing, 30000)
    return () => clearInterval(interval)
  }, [loggedIn])

  // Close menu when clicking outside or scrolling
  React.useEffect(() => {
    if (!openMenuId) return

    const handleClickOutside = (e) => {
      const target = e.target
      const menuButton = target.closest('[data-drawer-menu-button]')
      const menu = target.closest('[data-drawer-menu]')
      if (!menuButton && !menu) setOpenMenuId(null)
    }

    // Close on scroll so the fixed menu doesn't drift from its anchor
    const handleScroll = () => {
      setOpenMenuId(null)
      setDrawerDupeToastId(null)
      setDrawerDupeToastPos(null)
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('scroll', handleScroll, true)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('scroll', handleScroll, true)
    }
  }, [openMenuId])

  const deleteDesign = async (d) => {
    setDeleting(true)
    try {
      if (loggedIn) {
        const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
        await fetch(`${API}/api/designs/${d.id}`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', 'x-customer-token': token }
        })
      } else {
        const builds = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]')
        localStorage.setItem(LOCAL_KEY, JSON.stringify(builds.filter((b) => b.id !== d.id)))
        builderBus.refreshBuilds?.()
      }
      setDesigns((prev) => prev.filter((b) => b.id !== d.id))
    } catch {}
    setDeleting(false)
    setConfirmDeleteId(null)
  }

  const duplicateDesign = async (d) => {
    if (loggedIn && (totalBuildCount ?? designs.length) >= 24) return 'limit'
    try {
      if (loggedIn) {
        const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
        const response = await fetch(`${API}/api/designs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
          body: JSON.stringify({
            name: `Copy of ${d.name || 'Untitled Build'}`,
            config: d.config,
            thumbnail_url: d.thumbnail_url
          })
        })
        const data = await response.json()
        if (data.design) {
          setDesigns((prev) => [data.design, ...prev])
          builderBus.refreshBuilds?.()
          builderBus.incrementBuildCount?.()
        }
      } else {
        // Local storage duplicate
        const builds = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]')
        if (builds.length >= LOCAL_BUILDS_LIMIT) {
          return 'limit'
        }
        const newBuild = {
          ...d,
          id: Date.now(),
          name: `Copy of ${d.name || 'Untitled Build'}`
        }
        const updated = [newBuild, ...builds].slice(0, LOCAL_BUILDS_LIMIT)
        localStorage.setItem(LOCAL_KEY, JSON.stringify(updated))
        setDesigns(updated)
        builderBus.refreshBuilds?.()
      }
    } catch (e) {
      console.error('Duplicate failed:', e)
    }
  }

  const toggleFavorite = async (design) => {
    try {
      const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
      const newFavoriteState = !design.is_favorite

      await fetch(`${API}/api/designs/${design.id}/favorite`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
        body: JSON.stringify({ is_favorite: newFavoriteState })
      })

      // Update local state
      setDesigns((prev) => prev.map((d) => (d.id === design.id ? { ...d, is_favorite: newFavoriteState } : d)))

      // Refresh the main builds list if it exists
      builderBus.refreshBuilds?.()
    } catch (e) {
      console.error('Toggle favorite failed:', e)
    }
  }

  const renameDesign = async (d) => {
    setRenameModalDesign(d)
    setRenameInputValue(d.name || 'Untitled Build')
  }

  const executeRename = async () => {
    const d = renameModalDesign
    const newName = renameInputValue.trim()
    if (!newName || !d) return

    try {
      if (loggedIn) {
        const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
        await fetch(`${API}/api/designs/${d.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
          body: JSON.stringify({ name: newName })
        })
        setDesigns((prev) => prev.map((item) => (item.id === d.id ? { ...item, name: newName } : item)))
      } else {
        const builds = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]')
        const updated = builds.map((b) => (b.id === d.id ? { ...b, name: newName } : b))
        localStorage.setItem(LOCAL_KEY, JSON.stringify(updated))
        setDesigns(updated)
        builderBus.refreshBuilds?.()
      }
    } catch (e) {
      console.error('Rename failed:', e)
    }
    setRenameModalDesign(null)
    setRenameInputValue('')
  }

  const shareDesign = async (d) => {
    const cfg = d.config || {}
    const cp = cfg.cartProperties || {}
    // Always generate a fresh fileid and POST config to Heroku so the share URL loads the build
    const fileid = Date.now()
    // Send the same auth + tagging shape as shareProduct so per-card shares
    // attribute correctly in admin (was previously anonymous because no
    // x-customer-token header was sent here).
    const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || ''
    let anonId = localStorage.getItem('ohma_anon_id')
    if (!anonId) {
      anonId = Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10)
      localStorage.setItem('ohma_anon_id', anonId)
    }
    fetch('https://customizer-test-nodejs-4c6a5bfd6dbd.herokuapp.com/api/receive-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'x-customer-token': token },
      body: JSON.stringify({ ...cfg, fileid, source: 'share', anon_id: anonId })
    }).catch(() => {})
    const shareUrl = `https://ohmaworld.com/products/build-your-ohma/?id=${fileid}`

    // Build proper labels with all details
    const colorLabel = cp['Body Color'] || (colorNames && colorNames[cfg.color]) || cfg.color || '—'
    const screenTypeLabel = cp['Screen Type'] || screenTypeOptions[cfg.screenType]?.label || cfg.screenType || '—'
    const screenColorLabel = cp['Screen Color'] || cfg.screenColor || '—'
    const fabricColorLabel = cp['Fabric Color'] || cfg.fabricColor || '—'
    const bottomFabricLabel = cp['Bottom Fabric Color'] || cfg.bottomFabricColor || null
    const bottomScreenLabel = cp['Bottom Screen Type'] || cfg.bottomScreenType || null
    const micLabel = cp['Mic Type'] || cfg.microphoneType || '—'
    const popLabel = cfg.popFilterEnabled
      ? cfg.popFilterMaterial === 'plastic'
        ? 'Black (Plastic)'
        : (['#232221', '#1c1d1d', '#1a1a1a', '#000000', '#000'].includes((cfg.popFilterColor || '').toLowerCase())
            ? 'Black'
            : (colorNames && colorNames[cfg.popFilterColor]) || cfg.popFilterColor || '—') + ' (Metal)'
      : null

    const shareText = [
      'Check out my custom Ohma mic build:',
      shareUrl,
      '',
      micLabel ? `Microphone Type: ${micLabel}` : null,
      colorLabel ? `Body Color: ${colorLabel}` : null,
      screenTypeLabel ? `Screen Type: ${screenTypeLabel}` : null,
      screenColorLabel ? `Screen Color: ${screenColorLabel}` : null,
      fabricColorLabel ? `Fabric Color: ${fabricColorLabel}` : null,
      bottomFabricLabel ? `Bottom Fabric Color: ${bottomFabricLabel}` : null,
      bottomScreenLabel ? `Bottom Screen Type: ${bottomScreenLabel}` : null,
      popLabel ? `Pop Filter: ${popLabel}` : null
    ]
      .filter((l) => l !== null)
      .join('\n')

    // Try sharing with the thumbnail image if available
    const thumbnailUrl = loggedIn ? d.share_thumbnail_url || d.thumbnail_url : d.share_thumbnail_base64 || d.thumbnail_base64
    if (thumbnailUrl && navigator.share && navigator.canShare) {
      try {
        let blob
        if (thumbnailUrl.startsWith('data:')) {
          // Base64 image
          const response = await fetch(thumbnailUrl)
          blob = await response.blob()
        } else {
          // URL image
          const response = await fetch(thumbnailUrl)
          blob = await response.blob()
        }
        const file = new File([blob], `my-ohma-mic-build-${fileid}.png`, { type: 'image/png' })
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ text: shareText, files: [file] })
          return
        }
      } catch (e) {
        if (e.name === 'AbortError') return
        // Fall through to URL-only share
      }
    }

    // Try URL-only share (no image)
    if (navigator.share) {
      try {
        await navigator.share({ text: shareText, url: shareUrl })
        return
      } catch (e) {
        if (e.name === 'AbortError') return
      }
    }

    // Fallback: copy full text + link to clipboard
    try {
      await navigator.clipboard.writeText(shareText)
      alert('Link copied to clipboard!')
    } catch (e) {
      console.error('Share failed:', e)
    }
  }

  // Load data whenever drawer opens
  React.useEffect(() => {
    if (!open) {
      setOpenMenuId(null) // Close menu when drawer closes
      setDrawerDupeToastId(null)
      setDrawerDupeToastPos(null)
      return
    }
    setLoaded(false)
    let cancelled = false
    ensureFreshLogin().then(() => {
      if (cancelled) return
      const token = localStorage.getItem('ohma_ctoken') || window.SHOPIFY_CUSTOMER_TOKEN || null
      const exp = localStorage.getItem('ohma_ctoken_exp')
      const tokenValid = token && (!exp || Date.now() < Number(exp))

      if (tokenValid) {
        setLoggedIn(true)
        fetch(`${API}/api/designs`, {
          headers: { 'Content-Type': 'application/json', 'x-customer-token': token }
        })
          .then((r) => r.json())
          .then((data) => {
            const list = (data.designs || []).slice(0, 6) // Cap at 6 for logged in
            setDesigns(list)
            setLoaded(true)
          })
          .catch(() => {
            setLoaded(true)
          })
      } else {
        setLoggedIn(false)
        try {
          const local = JSON.parse(localStorage.getItem(LOCAL_KEY) || '[]').slice(0, 3)
          setDesigns(local)
        } catch {
          setDesigns([])
        }
        setLoaded(true)
      }
    })
    return () => {
      cancelled = true
    }
  }, [open, refreshKey])

  // Let the save flow drop a picture into a card once its upload finishes
  React.useEffect(() => {
    builderBus.patchDrawerDesign = ({ id, ...patch }) => {
      if (!id) return
      setDesigns((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)))
    }
    return () => {
      builderBus.patchDrawerDesign = null
    }
  }, [])

  // Close on Escape
  React.useEffect(() => {
    if (!open) return
    const handler = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  const cardImg = (d) => {
    if (loggedIn) return d.share_thumbnail_url || d.thumbnail_url || null
    return d.share_thumbnail_base64 || d.thumbnail_base64 || null
  }

  const cardName = (d) => d.name || 'Untitled Build'

  const cardSub = (d) => {
    const dateStr = d.updated_at || d.created_at
    if (!dateStr) return null
    const now = new Date()
    const saved = new Date(dateStr)
    const diffMs = now - saved
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 1) return 'Saved just now'
    if (diffMins < 60) return `Saved ${diffMins} min${diffMins === 1 ? '' : 's'} ago`
    if (diffHours < 24) return `Saved ${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    return `Saved ${diffDays} day${diffDays === 1 ? '' : 's'} ago`
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 3999,
          background: 'rgba(0,0,0,0.18)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 280ms ease'
        }}
      />

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: open ? 0 : -420,
          bottom: 0,
          width: 420,
          zIndex: 4000,
          background: '#faf8f3',
          boxShadow: open ? '-4px 0 32px rgba(0,0,0,0.18)' : 'none',
          transition: 'right 320ms cubic-bezier(0.32,0,0.18,1)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '16px 0 0 16px'
        }}>
        {/* Header */}
        <div
          style={{
            padding: '118px 20px 16px',
            borderBottom: '1px solid #e8e8e6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            background: '#faf8f3'
          }}>
          <div>
            <div
              style={{
                fontFamily: FONT_H,
                fontWeight: 700,
                fontSize: '1.1em',
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                color: '#333',
                position: 'relative'
              }}>
              My Mic Builds
            </div>
            <div style={{ fontFamily: FONT_B, fontSize: 13, color: '#aaa', marginTop: 3 }}>
              {loaded ? (designs.length === 0 ? 'No builds saved yet' : `${designs.length} recent build${designs.length !== 1 ? 's' : ''}`) : 'Loading…'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#333',
              padding: 4,
              flexShrink: 0
            }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Cards grid — dynamic layout */}
        <div
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            padding: '16px'
          }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: designs.length <= 3 ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: 12,
              alignContent: 'start',
              justifyContent: 'center',
              maxWidth: designs.length <= 3 ? '340px' : '100%',
              margin: '0 auto'
            }}>
            {!loaded ? (
              <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                <svg
                  style={{ animation: 'ohma-spin 0.9s linear infinite' }}
                  width="28"
                  height="28"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={COLOR_BLUE}
                  strokeWidth="2.5"
                  strokeLinecap="round">
                  <style>{`@keyframes ohma-spin{to{transform:rotate(360deg)}}`}</style>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </div>
            ) : designs.length === 0 ? (
              <div
                style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px 16px', color: '#bbb', fontFamily: FONT_B, fontSize: 13, lineHeight: 1.7 }}>
                Save your first build using the
                <br />
                save icon above.
              </div>
            ) : (
              designs.map((d, i) => {
                const img = cardImg(d)
                const name = cardName(d)
                const sub = cardSub(d)
                const isActive = currentDesignId && d.id === currentDesignId && !hasUnsavedChanges
                const isInCart = d.source === 'cart'
                const menuOpen = openMenuId === (d.id || i)

                return (
                  <div
                    key={d.id || i}
                    style={{
                      background: '#fff',
                      borderRadius: 16,
                      border: '2px solid #ebebea',
                      outline: isActive ? `2px solid ${COLOR_SALMON}` : 'none',
                      outlineOffset: isActive ? '1px' : '0',
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'outline 0.2s',
                      zIndex: menuOpen ? 100 : 'auto'
                    }}>
                    {/* Card image */}
                    <div
                      onClick={() => {
                        onClose()
                        onOpenMyBuilds()
                        setTimeout(() => builderBus.openBuildsDetail?.(d), 160)
                      }}
                      onMouseEnter={(e) => {
                        const img = e.currentTarget.querySelector('img')
                        if (img) img.style.transform = 'scale(1.05)'
                      }}
                      onMouseLeave={(e) => {
                        const img = e.currentTarget.querySelector('img')
                        if (img) img.style.transform = 'scale(1)'
                      }}
                      style={{
                        width: '100%',
                        aspectRatio: '3/4',
                        background: COLOR_BLUE,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        borderRadius: '14px 14px 0 0',
                        cursor: 'pointer'
                      }}>
                      {img ? (
                        <img src={img} alt={name} style={{ width: '100%', height: '100%', objectFit: 'contain', transition: 'transform 0.2s ease' }} />
                      ) : (
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round">
                          <rect x="3" y="3" width="18" height="18" rx="3" />
                          <circle cx="8.5" cy="8.5" r="1.5" />
                          <path d="M21 15l-5-5L5 21" />
                        </svg>
                      )}

                      {/* In Cart badge - top left */}
                      {isInCart && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 10,
                            left: 10,
                            background: COLOR_SALMON,
                            color: '#fff',
                            fontFamily: 'Jost, sans-serif',
                            fontWeight: 600,
                            fontSize: 11,
                            padding: '4px 10px',
                            borderRadius: 12,
                            display: 'inline-block'
                          }}>
                          In Cart
                        </div>
                      )}

                      {/* Three dots menu button */}
                      <button
                        data-drawer-menu-button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!menuOpen) {
                            const rect = e.currentTarget.getBoundingClientRect()
                            setDrawerMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right })
                          }
                          setOpenMenuId(menuOpen ? null : d.id || i)
                        }}
                        style={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: '#fff',
                          border: 'none',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          boxShadow: 'none',
                          transition: 'all 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f5f5f5'
                          e.currentTarget.style.transform = 'scale(1.05)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fff'
                          e.currentTarget.style.transform = 'scale(1)'
                        }}>
                        <svg width="16" height="4" viewBox="0 0 16 4" fill="#333">
                          <circle cx="2" cy="2" r="2" />
                          <circle cx="8" cy="2" r="2" />
                          <circle cx="14" cy="2" r="2" />
                        </svg>
                      </button>
                    </div>

                    {/* Dropdown menu — portal to escape stacking contexts */}
                    {menuOpen &&
                      drawerMenuPos &&
                      ReactDOM.createPortal(
                        <div
                          data-drawer-menu
                          style={{
                            position: 'fixed',
                            top: drawerMenuPos.top,
                            right: drawerMenuPos.right,
                            background: '#fff',
                            borderRadius: 12,
                            border: '1px solid #e0e0e0',
                            boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                            zIndex: 999999,
                            minWidth: 180,
                            overflow: 'hidden'
                          }}>
                          {/* Menu items with separators */}
                          <>
                            {/* See Details */}
                            <div
                              onClick={(e) => {
                                e.stopPropagation()
                                onClose()
                                onOpenMyBuilds()
                                setTimeout(() => builderBus.openBuildsDetail?.(d), 160)
                              }}
                              style={{
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                color: '#333',
                                fontFamily: FONT_B,
                                fontSize: 14,
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                              <span>See Details</span>
                            </div>

                            {/* Share */}
                            <div
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenMenuId(null)
                                shareDesign(d)
                              }}
                              style={{
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                color: '#333',
                                fontFamily: FONT_B,
                                fontSize: 14,
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                                <polyline points="16 6 12 2 8 6" />
                                <line x1="12" y1="2" x2="12" y2="15" />
                              </svg>
                              <span>Share</span>
                            </div>

                            {/* Separator */}
                            <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0' }} />

                            {/* Rename */}
                            <div
                              onClick={(e) => {
                                e.stopPropagation()
                                setOpenMenuId(null)
                                renameDesign(d)
                              }}
                              style={{
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                color: '#333',
                                fontFamily: FONT_B,
                                fontSize: 14,
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                              </svg>
                              <span>Rename</span>
                            </div>

                            {/* Favorite - only show when logged in */}
                            {loggedIn && (
                              <div
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setOpenMenuId(null)
                                  toggleFavorite(d)
                                }}
                                style={{
                                  padding: '12px 16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 12,
                                  cursor: 'pointer',
                                  transition: 'background 0.15s',
                                  color: '#333',
                                  fontFamily: FONT_B,
                                  fontSize: 14,
                                  fontWeight: 500
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                                <svg
                                  width="18"
                                  height="18"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round">
                                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                </svg>
                                <span>Favorite</span>
                              </div>
                            )}

                            {/* Duplicate */}
                            <div
                              onClick={(e) => {
                                e.stopPropagation()
                                const result = duplicateDesign(d)
                                Promise.resolve(result).then((r) => {
                                  setOpenMenuId(null)
                                  if (r === 'limit') {
                                    setDrawerDupeToastId(d.id || i)
                                    setDrawerDupeToastPos(drawerMenuPos)
                                    setTimeout(() => {
                                      setDrawerDupeToastId(null)
                                      setDrawerDupeToastPos(null)
                                    }, 3000)
                                  }
                                })
                              }}
                              style={{
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                color: '#333',
                                fontFamily: FONT_B,
                                fontSize: 14,
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                              </svg>
                              <span>Duplicate</span>
                            </div>

                            {/* Separator */}
                            <div style={{ height: 1, background: '#e0e0e0', margin: '4px 0' }} />

                            {/* Delete */}
                            <div
                              onClick={(e) => {
                                e.stopPropagation()
                                setConfirmDeleteId(d.id || i)
                                setOpenMenuId(null)
                              }}
                              style={{
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 12,
                                cursor: 'pointer',
                                transition: 'background 0.15s',
                                color: '#e63946',
                                fontFamily: FONT_B,
                                fontSize: 14,
                                fontWeight: 500
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}>
                              <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                              <span>Delete</span>
                            </div>
                          </>
                        </div>,
                        document.body
                      )}

                    {/* Dupe limit toast - portalled to escape stacking context */}
                    {drawerDupeToastId === (d.id || i) &&
                      drawerDupeToastPos &&
                      ReactDOM.createPortal(
                        <div
                          data-dupe-toast
                          style={{
                            position: 'fixed',
                            top: drawerDupeToastPos.top,
                            right: drawerDupeToastPos.right,
                            background: '#000',
                            color: '#fff',
                            fontSize: '11px',
                            fontFamily: FONT_B,
                            fontWeight: 600,
                            padding: '6px 10px',
                            borderRadius: '6px',
                            zIndex: 999999,
                            whiteSpace: 'nowrap',
                            letterSpacing: 'normal',
                            textTransform: 'none',
                            lineHeight: 1.4
                          }}>
                          {loggedIn ? (
                            'Save limit reached (24 builds max)'
                          ) : (
                            <span>
                              Save this build and more by{' '}
                              <span
                                onClick={() => {
                                  onClose()
                                  onOpenMyBuilds()
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                                onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                                style={{ textDecoration: 'underline', cursor: 'pointer' }}>
                                signing in
                              </span>
                              .
                            </span>
                          )}
                        </div>,
                        document.body
                      )}

                    {/* Card content - Name, subtitle, badges, button */}
                    <div style={{ padding: '12px 14px 14px' }}>
                      <div
                        style={{
                          fontFamily: FONT_H,
                          fontWeight: 700,
                          fontSize: '0.62em',
                          letterSpacing: '0.06em',
                          color: '#222',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textTransform: 'uppercase',
                          marginBottom: 4
                        }}>
                        {name}
                      </div>
                      <div
                        style={{
                          fontFamily: FONT_B,
                          fontSize: 11,
                          color: '#808082',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          marginBottom: 8
                        }}>
                        {sub}
                      </div>

                      {/* Status badges - removed, now on image */}
                      <div style={{ marginBottom: 10 }}></div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!isActive) onLoadConfirm(d)
                        }}
                        disabled={isActive}
                        style={{
                          width: '100%',
                          padding: '10px 0',
                          background: isActive ? COLOR_SALMON : '#eeeeef',
                          color: isActive ? '#fff' : '#555',
                          border: 'none',
                          borderRadius: '999px',
                          cursor: isActive ? 'default' : 'pointer',
                          fontFamily: FONT_H,
                          fontWeight: 700,
                          fontSize: '0.58em',
                          letterSpacing: '0.1em',
                          textTransform: 'uppercase',
                          transition: 'background 0.15s',
                          opacity: isActive ? 1 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (!isActive) e.currentTarget.style.background = '#dcdcdb'
                        }}
                        onMouseLeave={(e) => {
                          if (!isActive) e.currentTarget.style.background = '#eeeeef'
                        }}>
                        {isActive ? 'Selected' : 'Load Build'}
                      </button>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            flexShrink: 0,
            padding: '16px 20px 24px',
            borderTop: '1px solid #e8e8e6',
            background: '#faf8f3',
            display: 'flex',
            flexDirection: 'column',
            gap: 10
          }}>
          {/* Login nudge for logged-out users */}
          {!loggedIn && (
            <>
              <div style={{ fontFamily: FONT_B, fontSize: 13, color: '#808082', lineHeight: 1.6, textAlign: 'center', marginBottom: 8 }}>
                Save this build and more by{' '}
                <span
                  onClick={() => {
                    onClose()
                    onOpenMyBuilds()
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.7')}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
                  style={{
                    color: highlightSignIn ? '#e98969' : '#333',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontWeight: highlightSignIn ? 700 : 'inherit',
                    transition: 'color 0.3s, font-weight 0.3s'
                  }}>
                  signing in
                </span>
                .
              </div>
              <button
                onClick={() => {
                  onClose()
                  onOpenMyBuilds()
                }}
                style={{
                  width: '100%',
                  padding: '13px',
                  background: COLOR_BLUE,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '999px',
                  cursor: 'pointer',
                  fontFamily: FONT_H,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.12em',
                  transition: 'background 0.15s',
                  boxShadow: 'none',
                  marginBottom: 8
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#7ab3d0')}
                onMouseLeave={(e) => (e.currentTarget.style.background = COLOR_BLUE)}>
                Sign In or Create Account
              </button>
            </>
          )}

          {/* View All Builds - text only, no button styling */}
          <div
            onClick={() => {
              onClose()
              onOpenMyBuilds()
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#91c3de'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#333'
            }}
            style={{
              width: '100%',
              padding: '13px 0',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              fontFamily: FONT_H,
              fontWeight: 700,
              fontSize: '0.65em',
              letterSpacing: '0.12em',
              color: '#333',
              transition: 'color 0.15s'
            }}>
            <img src="https://pub-e2a1a41ee7e84eefbab9b339c21acfb0.r2.dev/Gallery.svg" width="14" height="14" alt="Gallery" style={{ display: 'block' }} />
            <span style={{ textDecoration: 'underline' }}>View All Builds</span>
            {designs.length > 0 && (
              <span
                style={{
                  background: '#E89B88',
                  color: '#fff',
                  fontSize: '11px',
                  fontWeight: 700,
                  fontFamily: "'Jost', system-ui, sans-serif",
                  width: '23px',
                  height: '23px',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: '1',
                  flexShrink: 0,
                  marginLeft: '2px',
                  textAlign: 'center',
                  paddingLeft: '1px'
                }}>
                {totalBuildCount != null ? totalBuildCount : loggedIn ? designs.length : `${designs.length}+`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Rename Modal */}
      {renameModalDesign && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            animation: 'fadeIn 200ms ease'
          }}
          onClick={() => {
            setRenameModalDesign(null)
            setRenameInputValue('')
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: 'none'
            }}>
            <div
              style={{
                fontFamily: FONT_H,
                fontWeight: 700,
                fontSize: '0.9em',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#222',
                marginBottom: 16
              }}>
              Rename Build
            </div>
            <input
              type="text"
              value={renameInputValue}
              onChange={(e) => setRenameInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') executeRename()
                if (e.key === 'Escape') {
                  setRenameModalDesign(null)
                  setRenameInputValue('')
                }
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '15px',
                fontFamily: FONT_B,
                border: '2px solid #e0e0e0',
                borderRadius: 8,
                outline: 'none',
                marginBottom: 20,
                transition: 'border-color 180ms'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = COLOR_SALMON
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0'
              }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setRenameModalDesign(null)
                  setRenameInputValue('')
                }}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  color: '#555',
                  border: 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontFamily: FONT_H,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  transition: 'background 180ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e8e8e8'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f5f5f5'
                }}>
                Cancel
              </button>
              <button
                onClick={executeRename}
                style={{
                  padding: '10px 20px',
                  background: COLOR_SALMON,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontFamily: FONT_H,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  transition: 'opacity 180ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1'
                }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteId !== null && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 5000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.4)',
            animation: 'fadeIn 200ms ease'
          }}
          onClick={() => setConfirmDeleteId(null)}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '24px',
              maxWidth: '400px',
              width: '90%',
              boxShadow: 'none'
            }}>
            <div
              style={{
                fontFamily: FONT_H,
                fontWeight: 700,
                fontSize: '0.9em',
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: '#222',
                marginBottom: 8
              }}>
              Delete this build?
            </div>
            <div style={{ fontFamily: FONT_B, fontSize: 14, color: '#666', marginBottom: 20, lineHeight: 1.5 }}>This can't be undone.</div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmDeleteId(null)}
                style={{
                  padding: '10px 20px',
                  background: '#f5f5f5',
                  color: '#555',
                  border: 'none',
                  borderRadius: 999,
                  cursor: 'pointer',
                  fontFamily: FONT_H,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  transition: 'background 180ms'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#e8e8e8'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#f5f5f5'
                }}>
                Cancel
              </button>
              <button
                onClick={() => {
                  const design = designs.find((d) => d.id === confirmDeleteId || designs.findIndex((d2) => d2.id === null) === confirmDeleteId)
                  if (design) deleteDesign(design)
                }}
                disabled={deleting}
                style={{
                  padding: '10px 20px',
                  background: '#e63946',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 999,
                  cursor: deleting ? 'not-allowed' : 'pointer',
                  fontFamily: FONT_H,
                  fontWeight: 700,
                  fontSize: '0.65em',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  opacity: deleting ? 0.7 : 1,
                  transition: 'background 180ms'
                }}
                onMouseEnter={(e) => {
                  if (!deleting) e.currentTarget.style.background = '#c0303b'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#e63946'
                }}>
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
